import type { CreateRoomResponse, RoomInfoResponse, RoomListResponse } from '@listen-draw/shared';

const base = import.meta.env.VITE_SERVER_URL?.replace(/\/$/, '') ?? '';

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const headers: Record<string, string> = { accept: 'application/json' };
  if (init?.body) headers['content-type'] = 'application/json';
  const res = await fetch(`${base}${path}`, {
    ...init,
    signal: init?.signal ?? AbortSignal.timeout(12000),
    headers: { ...headers, ...(init?.headers as Record<string, string> | undefined) },
  });
  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as { error?: string; message?: string };
    throw new ApiError(res.status, body.error ?? body.message ?? `HTTP ${res.status}`);
  }
  return (await res.json()) as T;
}

export class ApiError extends Error {
  constructor(
    public status: number,
    message: string,
  ) {
    super(message);
  }
}

export const api = {
  listRooms: () => request<RoomListResponse>('/api/rooms'),
  createRoom: () => request<CreateRoomResponse>('/api/rooms', { method: 'POST' }),
  roomInfo: (code: string) => request<RoomInfoResponse>(`/api/rooms/${encodeURIComponent(code)}`),
  config: () => request<{ aiEnabled: boolean; version: string }>('/api/config'),
};

/** ルームコードはクエリで渡す（Cloudflare 版はこれで Durable Object にルーティングする。Node 版は無視） */
export function wsUrl(roomCode: string): string {
  const q = `?room=${encodeURIComponent(roomCode)}`;
  if (base) return base.replace(/^http/, 'ws') + '/ws' + q;
  const proto = location.protocol === 'https:' ? 'wss:' : 'ws:';
  return `${proto}//${location.host}/ws${q}`;
}

export function playUrl(code: string): string {
  return `${location.origin}/play/${code}`;
}

// ---- localStorage helpers（失敗しても落ちない） ----
export const storage = {
  get(key: string): string | null {
    try {
      return localStorage.getItem(key);
    } catch {
      return null;
    }
  },
  set(key: string, value: string) {
    try {
      localStorage.setItem(key, value);
    } catch {
      /* ignore */
    }
  },
  remove(key: string) {
    try {
      localStorage.removeItem(key);
    } catch {
      /* ignore */
    }
  },
};
export const keys = {
  host: (code: string) => `ld.host.${code}`,
  player: (code: string) => `ld.player.${code}`,
  name: 'ld.name',
  voice: 'ld.voice',
  showTexts: 'ld.host.showTexts',
};
